import { useEffect, useState, useRef, useCallback } from 'react';
import { apiGet, apiPost, apiDelete } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { FileText, Image as ImageIcon, Upload, Trash2, Eye, Loader2, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

const readAsDataURL = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const dataURLtoBlob = (dataURL) => {
  const [meta, b64] = dataURL.split(',');
  const mime = (meta.match(/:(.*?);/) || [])[1] || 'application/octet-stream';
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
};

// Reusable worker-document manager. Documents live in a dedicated collection
// keyed by caseId and are purged when the linked visa is deleted.
const WorkerDocuments = ({ caseId, readOnly = false, compact = false }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [opening, setOpening] = useState(null);
  const inputRef = useRef(null);

  const loadDocs = useCallback(async () => {
    if (!caseId) { setLoading(false); return; }
    setLoading(true);
    setLoadError(false);
    try {
      const res = await apiGet(`/api/cases/${caseId}/documents`);
      // apiGet returns an array ([]) on failure, but this endpoint returns
      // {documents:[...]} on success — use that to detect a real load error.
      if (Array.isArray(res)) {
        setLoadError(true);
        setDocs([]);
      } else {
        setDocs(res?.documents || []);
      }
    } catch (e) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      const isValid = file.type.startsWith('image/') || file.type === 'application/pdf';
      if (!isValid) { toast.error(`${file.name}: صيغة غير مدعومة (صور و PDF فقط)`); continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: الحجم كبير جداً (الحد 10MB)`); continue; }
      try {
        const dataUrl = await readAsDataURL(file);
        await apiPost(`/api/cases/${caseId}/documents`, {
          fileName: file.name,
          mimeType: file.type,
          fileData: dataUrl,
        });
        ok += 1;
      } catch (e) {
        toast.error(`فشل رفع ${file.name}`);
      }
    }
    if (ok > 0) toast.success(`✅ تم رفع ${ok} مستند`);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
    loadDocs();
  };

  const handleView = async (doc) => {
    setOpening(doc.id);
    try {
      const full = await apiGet(`/api/documents/${doc.id}`);
      if (!full?.fileData) { toast.error('تعذّر فتح المستند'); return; }
      const blob = dataURLtoBlob(full.fileData);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('تعذّر فتح المستند');
    } finally {
      setOpening(null);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`حذف المستند "${doc.fileName}" نهائياً؟`)) return;
    try {
      await apiDelete(`/api/documents/${doc.id}`);
      toast.success('تم حذف المستند');
      loadDocs();
    } catch (e) {
      toast.error('فشل حذف المستند');
    }
  };

  return (
    <div className="space-y-3" data-testid="worker-documents">
      {!readOnly && (
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,.pdf,application/pdf"
            multiple
            onChange={handleUpload}
            className="hidden"
            data-testid="worker-documents-input"
          />
          <Button
            type="button"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading || !caseId}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="worker-documents-upload-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            رفع مستندات / Upload
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-3">
          <Loader2 className="h-4 w-4 animate-spin" /> جارٍ التحميل...
        </div>
      ) : loadError ? (
        <div className="flex items-center justify-between gap-2 text-red-400 text-sm py-3" data-testid="worker-documents-error">
          <span>تعذّر تحميل المستندات</span>
          <Button type="button" size="sm" variant="outline" onClick={loadDocs} className="border-red-500/40 text-red-300 h-7">
            إعادة المحاولة
          </Button>
        </div>
      ) : docs.length === 0 ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-3" data-testid="worker-documents-empty">
          <Paperclip className="h-4 w-4" /> لا توجد مستندات
        </div>
      ) : (
        <div className={compact ? 'space-y-2' : 'grid grid-cols-1 gap-2'}>
          {docs.map((doc) => {
            const isPdf = doc.mimeType === 'application/pdf';
            const Icon = isPdf ? FileText : ImageIcon;
            return (
              <div
                key={doc.id}
                className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-800/60 border border-white/5"
                data-testid={`worker-document-${doc.id}`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isPdf ? 'bg-red-500/15 text-red-400' : 'bg-blue-500/15 text-blue-400'}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{doc.fileName}</p>
                  <p className="text-slate-500 text-xs">{doc.sizeKB} KB</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleView(doc)}
                  disabled={opening === doc.id}
                  className="h-8 w-8 rounded-lg flex items-center justify-center text-cyan-400 hover:bg-cyan-500/10"
                  title="عرض"
                  data-testid={`view-document-${doc.id}`}
                >
                  {opening === doc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                </button>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => handleDelete(doc)}
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-500/10"
                    title="حذف"
                    data-testid={`delete-document-${doc.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WorkerDocuments;
